import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, Output,
  EventEmitter } from '@angular/core';
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  BaseComponent,
  WmsDialogBodyComponent,
  WmsDialogComponent,
  WmsDialogFooterComponent,
  WmsDialogHeaderComponent
} from '@wms/components';
import { forEach, isNil, isString, isEmpty, clone, cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { WmsCriteriaGroupComponent } from '../wms-criteria-group/wms-criteria-group.component';
import { DialogService, FilterCriteriaService, LiveWidgetUtilsService } from '@wms/services';
import { runtimeEventManager } from "@wm/core";
import { CommonWidgetDirective } from "@wms/directives";

@Component({
  standalone: true,
  selector: 'wms-criteria-builder',
  templateUrl: './wms-criteria-builder.component.html',
  styleUrls: ['./wms-criteria-builder.component.less'],
  imports: [WmsCriteriaGroupComponent, WmsDialogComponent,
    CommonModule, WmsDialogHeaderComponent, WmsDialogBodyComponent,
    WmsDialogFooterComponent, FormsModule, CommonWidgetDirective]
})
export class WmsCriteriaBuilderComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filterFields: any;
  @Input() filterExpressions: any;
  @Input() liveWidgetSettingsService: any;
  @Input() isDialog: boolean = false;
  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() onChangeCallback: EventEmitter<any> = new EventEmitter<any>();
  @Output() onBindCallback: EventEmitter<any> = new EventEmitter<any>();

  json: any;
  public filterCriteriaService: any;
  queryPreviewSubscription: Subscription;
  private dialogService: DialogService;
  private query: any;
  sanitisedQuery: any
  private destroySubscription: Subscription = new Subscription();

  constructor(private liveWidgetUtilsService: LiveWidgetUtilsService) {
    super();
    this.filterCriteriaService = inject(FilterCriteriaService);
    this.dialogService = inject(DialogService);
    this.queryPreviewSubscription = this.filterCriteriaService.queryPreviewObs().subscribe(() => {
      this.onFilterExpChange();
    });

    runtimeEventManager.off('savecloseCriteriaDialog');
    runtimeEventManager.on('savecloseCriteriaDialog').subscribe((id: string) => {
      this.saveAndCloseCriteriaDialog(id);
    });
    runtimeEventManager.off('closeCriteriaDialog');
    runtimeEventManager.on('closeCriteriaDialog').subscribe((id: string) => {
      this.closeCriteriaDialog(id);
    });
  }

  ngOnInit(): void {
    if (isString(this.filterExpressions)) {
      this.filterExpressions = JSON.parse(this.filterExpressions);
    }
    this.initData();
    this.filterCriteriaService.filterFields = this.filterFields;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterExpressions'] && changes['filterExpressions']['currentValue'] !== changes['filterExpressions']['previousValue'] ) {
      this.onFilterExpChange();
    }
  }

  initData() {
    this.json = null;
    this.filterCriteriaService.data['fields'] = [];

    forEach(this.filterFields, (columnObj) => {
      /**
       * creating a new property display name which is used to show the valuesin the dropdown.
       * We are doing this to avoid the entity names being shown up in the dropdown like department - instead of deptId
       * ignoring the blob/clob type in the query builder
       */
      if (columnObj.type !== 'blob' && columnObj.type !== 'clob') {
        columnObj.displayName = columnObj.field;
        this.filterCriteriaService.data.fields.push(columnObj.displayName);
        this.filterCriteriaService.data.fieldsTypeMap[columnObj.displayName] = columnObj.dataType;
      }
    });

    if (isNil(this.filterExpressions) || isEmpty(this.filterExpressions)) {
      this.filterExpressions = cloneDeep(this.filterCriteriaService.CRITERIA_GROUP);
    } else if (isString(this.filterExpressions)) {
      this.filterExpressions = JSON.parse(this.filterExpressions);
    }
    // this is to avoid invalid state. Always keep one rule in the group
    if (!this.filterExpressions.rules.length) {
      this.filterExpressions.rules.push(clone(this.filterCriteriaService.CRITERIA));
    }
    this.filterCriteriaService.filterExpressions = this.filterExpressions;
    this.setOutput();
  }

  onFilterExpChange() {
    if (isString(this.filterExpressions)) {
      this.filterExpressions = JSON.parse(this.filterExpressions);
    }
    let newValue: any;
    if (isNil(this.filterExpressions) || isEmpty(this.filterExpressions)) {
      this.filterExpressions = cloneDeep(this.filterCriteriaService.CRITERIA_GROUP);
      this.filterCriteriaService.filterExpressions = this.filterExpressions;
    }
    newValue = this.filterExpressions;
    this.json = JSON.stringify(newValue, null, 2);
    this.filterCriteriaService.data.queryPreview = this.liveWidgetUtilsService.compute(!isNil(newValue.rules) && Array.isArray(newValue.rules) ? newValue : "undefined");
    this.setOutput();
  }

  setOutput() {
    this.query = this.liveWidgetUtilsService.getQuery(this.filterExpressions);
    this.sanitisedQuery = this.getSanitisedQuery();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.queryPreviewSubscription) {
      this.queryPreviewSubscription.unsubscribe();
    }
    this.destroySubscription.unsubscribe();
  }

  /**
   * this will return the query string by stripping all the HTML tags. This is used to display the
   * content in the text area when filter criteria is used in dialog mode
   * @returns {*} String without HTML tags
   */
  getSanitisedQuery() {
    return this.query.replace(/(<([^>]+)>)/ig, '');
  }

  openFilterCriteriaDialog(id: string) {
    this.filterCriteriaService.previousResponse['filterExpressions'] = cloneDeep(this.filterExpressions);
    this.dialogService.open(id);
  }

  closeCriteriaDialog(id: string) {
    this.query = this.liveWidgetUtilsService.getQuery(this.filterCriteriaService.previousResponse.filterExpressions);
    this.sanitisedQuery = this.getSanitisedQuery();
    this.onChange.emit(this.filterCriteriaService.previousResponse.filterExpressions);
    this.dialogService.close(id);
  }

  saveAndCloseCriteriaDialog(id: string) {
    this.query = this.liveWidgetUtilsService.getQuery(this.filterCriteriaService.filterExpressions);
    this.sanitisedQuery = this.getSanitisedQuery();
    this.onChange.emit(this.filterCriteriaService.filterExpressions);
    this.dialogService.close(id);
  }
}
